import React from "react";
import Text, { TEXT_WRAP } from "@/bit/components/components.text";
import PropTypes from "prop-types";
import { getNormalizedSegment } from "@gcloud-npm/utils.enums.segments";
import {
  PRICE_LAYOUT,
  PRICE_SIZE,
  PRICE_DECORATION,
  PRICE_FONT_WEIGHT,
} from "./enums";

const parsePrice = (
  number,
  decimals,
  decimalChar = ",",
  thousandChar = ".",
) => {
  let res = number;
  res = res.toString().replace(",", thousandChar);
  res = parseFloat(res).toFixed(decimals);
  res = res.replace(".", decimalChar);
  const splitNum = res.split(decimalChar);
  splitNum[0] = splitNum[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandChar);
  if (splitNum[1] !== "00") {
    res = splitNum.join(decimalChar);
  } else {
    res = splitNum[0];
  }
  return res;
};

const normalizePrice = (amount, decimals) => {
  const priceToShow = amount;
  if (!priceToShow && priceToShow !== 0) {
    return null;
  }
  if (!(typeof priceToShow === "number" || typeof priceToShow === "string")) {
    return null;
  }
  return parsePrice(priceToShow, decimals);
};

//layout: compact, default
//size: xl, l, m, s

// Enums

const getMinMaxValues = (min, max) =>
  function (num) {
    return Math.min(Math.max(num, min), max);
  };

// layout -> pendiente
const getFinalSizes = (layout, size) => {
  const sizes = Object.values(PRICE_SIZE);

  const sizeIndex = sizes.includes(size)
    ? sizes.indexOf(size)
    : sizes.indexOf(PRICE_SIZE.S);

  const minMax = getMinMaxValues(0, sizes.length - 1);

  const dif = layout === PRICE_LAYOUT.COMPACT ? 4 : 2;
  return {
    currencySize: `price-${sizes[minMax(sizeIndex - dif)]}`,
    quoteSize: `price-${sizes[minMax(sizeIndex - dif)]}`,
    priceSize: `price-${sizes[minMax(sizeIndex)]}`,
  };
};

/**
 * Muestra información sobre el costo de un producto o servicio, generalmente en formato numérico con la moneda correspondiente.
 */
const Price = (props) => {
  const {
    layout,
    size,
    color,
    price,
    currency,
    quote,
    decoration,
    decorationUnity,
    fontType,
    fontTypeUnity,
    segment: segmentProp,
    cyData,
    cms,
    cmsHandler = null,
  } = props;

  if ((!price && price !== 0 && !cmsHandler) || price < 0) {
    return null;
  }

  const priceToShow = normalizePrice(price, 2);

  let finalLayout = PRICE_LAYOUT.DEFAULT;

  if (layout && Object.values(PRICE_LAYOUT).find((item) => item === layout)) {
    finalLayout = layout;
  }

  const segment = getNormalizedSegment(segmentProp);
  let className = `price price-layout--${finalLayout} price-size--${size}`;
  if (cmsHandler) {
    className = `${className} element-editable`;
  }

  const mySizes = getFinalSizes(layout, size);
  const { currencySize, priceSize, quoteSize } = mySizes;

  // cms placeholder
  if ((cms || process.env.NEXT_PUBLIC_ASTRO_MAKER) && price === "") {
    if (cmsHandler) {
      return (
        <div
          className={`${className} cms-text-placeholder`}
          data-cy={cyData}
          onClick={() => cmsHandler({ ...props, name: "price" })}
        >
          <Text color="dark_blue" text="Price" />
        </div>
      );
    } else return null;
  }

  return (
    <>
      <div
        className={className}
        data-cy={cyData}
        onClick={
          (cms || process.env.NEXT_PUBLIC_ASTRO_MAKER) && cmsHandler
            ? () => cmsHandler({ ...props, name: "price" })
            : null
        }
      >
        <Text
          className={`price--content height--${size?.toLowerCase() || "m"} bg-color--${color?.toLowerCase() || "grey5"}`}
          text={priceToShow}
          size={priceSize}
          color={color}
          cyData={cyData ? `${cyData}--content` : ""}
          display="inline"
          decoration={decoration}
          fontType={fontType}
          segment={segment}
        />
        <div className="price--unity">
          {currency && (
            <Text
              className="price--currency"
              text={currency}
              size={currencySize}
              color={color}
              cyData={cyData ? `${cyData}--currency` : ""}
              display="inline"
              decoration={decorationUnity || PRICE_DECORATION.NONE}
              fontType={fontTypeUnity || fontType}
              segment={segment}
              wrap={TEXT_WRAP.NOWRAP}
            />
          )}
          <div className="price--quote">
            {currency && quote && (
              <Text
                className="price--quote_text"
                text={`/${quote}`}
                cyData={cyData ? `${cyData}--quote_text` : ""}
                size={quoteSize}
                color={color}
                display="inline"
                decoration={decorationUnity || PRICE_DECORATION.NONE}
                fontType={fontTypeUnity || fontType}
                segment={segment}
              />
            )}
          </div>
        </div>
      </div>
      <style jsx>{`.price {
  &.price-layout--default {
    .price--unity {
      display: inline;
      padding-left: 0.25rem;
      .price--quote {
        display: inline;
      }
    }
  }
  .text-decoration--line-through::after {
    content: "";
    position: absolute;
    width: 100%;
    left: 0;
    right: 0;
    top: 58%; /* Puedes ajustar esto para mover la línea */
    height: 2px;
    background-color: grey;
    transform: translateY(-50%); /* centra verticalmente respecto al "top" */
  }

  .text-decoration--line-through {
    text-decoration: none;
    position: relative;

    &.height--xxxl::after {
      height: 10px;
    }
    &.height--xxl::after {
      height: 4px;
    }
    &.height--xl::after,
    &.height--l::after {
      height: 3px;
    }

    &.bg-color--movistarblue::after {
      background-color: #0066FF;
    }
    &.bg-color--bluelight::after {
      background-color: #D3EEFF;
    }
    &.bg-color--bluedark::after {
      background-color: #022D67;
    }
    &.bg-color--bluehigh::after {
      background-color: #005EEB;
    }
    &.bg-color--blue100::after {
      background-color: #F4F7F8;
    }
    &.bg-color--blue200::after {
      background-color: #E8F4FA;
    }
    &.bg-color--blue600::after {
      background-color: #2F7DFF;
    }
    &.bg-color--blue800::after {
      background-color: #0149B3;
    }
    &.bg-color--yellowlight::after {
      background-color: #FFF8D3;
    }
    &.bg-color--yellowdark::after {
      background-color: #5E4A09;
    }
    &.bg-color--yellow100::after {
      background-color: #FFF8E4;
    }
    &.bg-color--yellow200::after {
      background-color: #FFF3D1;
    }
    &.bg-color--yellow600::after {
      background-color: #BFA960;
    }
    &.bg-color--greenlight::after {
      background-color: #CEF7BF;
    }
    &.bg-color--greendark::after {
      background-color: #38552B;
    }
    &.bg-color--green100::after {
      background-color: #F5F9EA;
    }
    &.bg-color--green200::after {
      background-color: #EBF9DF;
    }
    &.bg-color--green600::after {
      background-color: #92B684;
    }
    &.bg-color--movistarwhite::after {
      background-color: #FFFAF5;
    }
    &.bg-color--white::after {
      background-color: #FFFFFF;
    }
    &.bg-color--white100::after {
      background-color: #FFFDFA;
    }
    &.bg-color--movistarblack::after {
      background-color: #262423;
    }
    &.bg-color--salmonlight::after {
      background-color: #FFC5AB;
    }
    &.bg-color--salmondark::after {
      background-color: #62301A;
    }
    &.bg-color--salmon100::after {
      background-color: #FEEEE6;
    }
    &.bg-color--salmon200::after {
      background-color: #FDE5D7;
    }
    &.bg-color--salmon600::after {
      background-color: #C0896F;
    }
    &.bg-color--red::after {
      background-color: #C10000;
    }
    &.bg-color--redhigh::after {
      background-color: #AD0000;
    }
    &.bg-color--gold::after {
      background-color: #926C00;
    }
    &.bg-color--goldhigh::after {
      background-color: #745600;
    }
    &.bg-color--emerald::after {
      background-color: #048239;
    }
    &.bg-color--emeraldhigh::after {
      background-color: #036D30;
    }
    &.bg-color--grey100::after {
      background-color: #FAF7F5;
    }
    &.bg-color--grey200::after {
      background-color: #F3EEEA;
    }
    &.bg-color--grey300::after {
      background-color: #DFDBD6;
    }
    &.bg-color--grey400::after {
      background-color: #9F9C99;
    }
    &.bg-color--grey500::after {
      background-color: #807D7B;
    }
    &.bg-color--grey600::after {
      background-color: #605E5C;
    }
    &.bg-color--grey700::after {
      background-color: #403F3D;
    }
    &.bg-color--yellow_prosegur::after {
      background-color: #FFD102;
    }
    &.bg-color--grey_prosegur::after {
      background-color: #3A3C3F;
    }
  }
  &.price-layout--compact {
    display: flex;
    align-items: center;
    .price--unity {
      display: inline-grid;
      align-items: center;
      padding-top: 0.3125rem;
      padding-left: 0.25rem;

      :global(.text) {
        line-height: 100% !important;
      }
    }
  }
  &-size--xxxl {
    .price--unity {
      align-self: stretch;
    }
    :global(.price--content) {
      font-size: 5rem !important;
      line-height: 4rem;
      align-self: stretch;
    }
  }
  @media screen and (min-width: 426px) {
    &-size--xxxl {
      :global(.price--content) {
        font-size: 6rem !important;
        line-height: 5rem;
      }
    }
  }
  @media screen and (min-width: 768px) {
    &-size--xxxl {
      :global(.price--content) {
        font-size: 7rem !important;
      }
    }
  }
  @media screen and (min-width: 1280px) {
    &-size--xxxl {
      :global(.price--content) {
        font-size: 9rem !important;
        line-height: 7rem;
      }
    }
  }
}
`}</style>
    </>
  );
};

Price.propTypes = {
  /**
   * {
   *  "info": "Define el valor correspondiente al precio. Puede ser formato string o numérico",
   *  "kind": "both",
   *  "beautifulName": "Price"
   * }
   */
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * {
   *  "info": "Define el valor correspondiente a la moneda. € es el valor más habitual",
   *  "kind": "both",
   *  "beautifulName": "Currency"
   * }
   */
  currency: PropTypes.string.isRequired,
  /**
   * {
   *  "info": "Define un valor complementario a la moneda, que se representará tras una /. mes es el valor más habitual",
   *  "kind": "both",
   *  "beautifulName": "Quote"
   * }
   */
  quote: PropTypes.string,
  /**
   * {
   *  "info": "Define el tamaño del precio. Puede ser uno de los valores definidos en PRICE_SIZE.",
   *  "kind": "both",
   *  "beautifulName": "Size",
   *  "values": "./enums.js:PRICE_SIZE"
   * }
   */
  size: PropTypes.string,
  /**
   * {
   *  "info": "Define la decoración del precio. Puede ser uno de los valores definidos en PRICE_DECORATION.",
   *  "kind": "both",
   *  "beautifulName": "Decoration",
   *  "values": "./enums.js:PRICE_DECORATION"
   * }
   */
  decoration: PropTypes.string,
  /**
   * {
   *  "info": "Define el color del texto del precio. Debe ser una cadena de texto representando el color.",
   *  "kind": "both",
   *  "beautifulName": "Color"
   * }
   */
  color: PropTypes.string,
  /**
   * {
   *  "info": "Define el tipo de letra de la cantidad",
   *  "kind": "both",
   *  "beautifulName": "Tipo fuente cantidad",
   *  "values": "./enums.js:PRICE_FONT_WEIGHT"
   * }
   */
  fontType: PropTypes.string,
  /**
   * {
   *  "info": "Define el tipo de letra del literal",
   *  "kind": "both",
   *  "beautifulName": "Tipo fuente literal",
   *  "values": "./enums.js:PRICE_FONT_WEIGHT"
   * }
   */
  fontTypeUnity: PropTypes.string,

  /**
   * {
   *  "info": "Define cómo se visualiza el precio y puede ser uno de los siguientes valores: 'default' o 'compact'.",
   *  "kind": "both",
   *  "beautifulName": "Layout",
   *  "values": "./enums.js:PRICE_LAYOUT"
   * }
   */
  layout: PropTypes.string,

  /**
   * {
   *  "info": "Objeto con información sobre las propiedades necesarias (id, timestamp...) para editar los valores en orion.",
   *  "kind": "dev",
   *  "beautifulName": "CMS"
   * }
   */
  cms: PropTypes.object,
};

Price.defaultProps = {
  price: "",
  quote: "",
  size: PRICE_SIZE.M,
  decoration: PRICE_DECORATION.NONE,
  color: "grey6",
  fontType: PRICE_FONT_WEIGHT.REGULAR,
  cms: null,
};

export { PRICE_LAYOUT, PRICE_SIZE, PRICE_DECORATION, PRICE_FONT_WEIGHT };
export default Price;
